import React, { useMemo, useState } from "react";
import { eachDayOfInterval, format, parseISO, startOfDay } from "date-fns";
import { eventLabel } from "./fieldAppDetailLabels";

function toMs(value) {
  if (value == null) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, value));
}

function formatIntervalRange(startMs, endMs) {
  if (startMs == null || endMs == null) return "—";
  return `${format(new Date(startMs), "dd/MM HH:mm")} — ${format(
    new Date(endMs),
    "dd/MM HH:mm"
  )}`;
}

export default function FieldAppActivityTimeline({
  lanes,
  domainStartMs,
  domainEndMs,
  onIntervalClick,
  t,
}) {
  const [selectedInterval, setSelectedInterval] = useState(null);

  const domain = useMemo(() => {
    const start = domainStartMs;
    const end = domainEndMs;
    if (start == null || end == null || end <= start) {
      return null;
    }
    return { start, end, span: end - start };
  }, [domainStartMs, domainEndMs]);

  if (!lanes || !lanes.length || !domain) {
    return null;
  }

  const handleIntervalClick = (lane, interval) => {
    const detail = {
      lane,
      interval,
      startMs: toMs(interval.start),
      endMs: toMs(interval.end),
    };
    setSelectedInterval(detail);
    if (typeof onIntervalClick === "function") {
      onIntervalClick(detail);
    }
  };

  return (
    <div className="field-app-activity-timeline">
      <div className="field-app-activity-timeline-head">
        <strong>{t("Linha do tempo — telemetria e máquinas")}</strong>
        <span className="field-app-activity-timeline-hint">
          {t("Clique em um trecho para ver detalhes")}
        </span>
      </div>

      {lanes.map((lane) => (
        <div className="field-app-activity-lane" key={lane.id}>
          <div
            className="field-app-activity-lane-label"
            title={lane.label}
            style={{ borderLeftColor: lane.color }}
          >
            {lane.label}
          </div>
          <div className="field-app-activity-lane-track">
            {(lane.intervals || []).map((interval, index) => {
              const startMs = toMs(interval.start);
              const endMs = toMs(interval.end);
              if (startMs == null || endMs == null) return null;

              const visibleStart = Math.max(startMs, domain.start);
              const visibleEnd = Math.min(endMs, domain.end);
              if (visibleEnd <= visibleStart) return null;

              const left = clampPercent(
                ((visibleStart - domain.start) / domain.span) * 100
              );
              const width = clampPercent(
                ((visibleEnd - visibleStart) / domain.span) * 100
              );

              return (
                <button
                  key={`${lane.id}-${index}`}
                  type="button"
                  className="field-app-activity-bar"
                  style={{
                    left: `${left}%`,
                    width: `${Math.max(width, 0.6)}%`,
                    backgroundColor: lane.color,
                  }}
                  title={formatIntervalRange(startMs, endMs)}
                  onClick={() => handleIntervalClick(lane, interval)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {selectedInterval ? (
        <div className="field-app-activity-detail">
          <div className="field-app-activity-detail-title">
            {selectedInterval.lane.type === "telemetry"
              ? t("Telemetria")
              : t("Máquina {{ident}}", {
                  ident: selectedInterval.lane.label,
                })}
          </div>
          <p>
            <strong>{t("Início")}:</strong>{" "}
            {format(new Date(selectedInterval.startMs), "dd/MM/yyyy HH:mm:ss")}
          </p>
          <p>
            <strong>{t("Fim")}:</strong>{" "}
            {format(new Date(selectedInterval.endMs), "dd/MM/yyyy HH:mm:ss")}
          </p>
          {selectedInterval.interval.endEvent ? (
            <p>
              <strong>{t("Encerramento")}:</strong>{" "}
              {eventLabel(selectedInterval.interval.endEvent)}
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn-xs btn-default"
            onClick={() => setSelectedInterval(null)}
          >
            {t("Fechar")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function buildGraphDayRanges(graphData) {
  if (!graphData || !graphData.length) return [];

  const buckets = [];
  graphData.forEach((point, index) => {
    const dayKey = format(new Date(point.time), "yyyy-MM-dd");
    const existing = buckets.find((item) => item.key === dayKey);
    if (existing) {
      existing.endIndex = index;
    } else {
      buckets.push({
        key: dayKey,
        label: format(new Date(point.time), "dd/MM/yyyy"),
        shortLabel: format(new Date(point.time), "dd/MM"),
        startIndex: index,
        endIndex: index,
      });
    }
  });

  return buckets;
}

/** Todos os dias do período escolhido no App de Campo (com ou sem GPS). */
export function buildCalendarDayRanges(dateFrom, dateTo, graphDayRanges) {
  const withDataFlag = (graphDayRanges || []).map((day) => ({
    ...day,
    hasData: true,
  }));

  if (!dateFrom || !dateTo) {
    return withDataFlag;
  }

  let start;
  let end;
  try {
    start = startOfDay(parseISO(dateFrom));
    end = startOfDay(parseISO(dateTo));
  } catch (error) {
    return withDataFlag;
  }

  if (end < start) {
    return withDataFlag;
  }

  const dataByKey = {};
  withDataFlag.forEach((day) => {
    dataByKey[day.key] = day;
  });

  return eachDayOfInterval({ start, end }).map((day) => {
    const key = format(day, "yyyy-MM-dd");
    const existing = dataByKey[key];
    if (existing) {
      return existing;
    }
    return {
      key,
      label: format(day, "dd/MM/yyyy"),
      shortLabel: format(day, "dd/MM"),
      startIndex: null,
      endIndex: null,
      hasData: false,
    };
  });
}

export function getDaysWithTracking(calendarDayRanges) {
  return (calendarDayRanges || []).filter((day) => day.hasData);
}

export function brushRangeFromSelectedDays(
  calendarDayRanges,
  selectedDayKeys,
  graphDataLength
) {
  if (!graphDataLength) {
    return { startIndex: 0, endIndex: 0 };
  }

  const lastIndex = graphDataLength - 1;
  const daysWithData = getDaysWithTracking(calendarDayRanges);

  if (!daysWithData.length || !selectedDayKeys.length) {
    return { startIndex: 0, endIndex: lastIndex };
  }

  const selected = daysWithData
    .filter((day) => selectedDayKeys.indexOf(day.key) !== -1)
    .sort((a, b) => a.key.localeCompare(b.key));

  if (!selected.length) {
    return { startIndex: 0, endIndex: lastIndex };
  }

  // Um dia: só aquele dia. Vários dias: intervalo contínuo do primeiro ao último
  // selecionado (inclui dias intermediários no gráfico, sem marcar os botões).
  return {
    startIndex: selected[0].startIndex,
    endIndex: selected[selected.length - 1].endIndex,
  };
}

export function graphDataForSelectedDays(
  graphData,
  graphDayRanges,
  selectedDayKeys
) {
  if (!graphData || !graphData.length || !selectedDayKeys || !selectedDayKeys.length) {
    return [];
  }

  const daysWithData = (graphDayRanges || []).map((day) => ({
    ...day,
    hasData: true,
  }));
  const range = brushRangeFromSelectedDays(
    daysWithData,
    selectedDayKeys,
    graphData.length
  );

  return graphData.slice(range.startIndex, range.endIndex + 1);
}

export function toggleDaySelection(selectedDayKeys, dayKey) {
  const index = selectedDayKeys.indexOf(dayKey);
  if (index === -1) {
    return selectedDayKeys.concat(dayKey).sort();
  }
  return selectedDayKeys.filter((key) => key !== dayKey);
}

/** Semana inteira ativa → um clique isola o dia; depois disso, clique alterna marca/desmarca. */
export function applyChartDayClick(selectedDayKeys, dayKey, allDayKeys) {
  const allSelected =
    allDayKeys.length > 0 &&
    allDayKeys.every((key) => selectedDayKeys.indexOf(key) !== -1);

  if (allSelected) {
    return [dayKey];
  }

  const index = selectedDayKeys.indexOf(dayKey);
  if (index === -1) {
    return selectedDayKeys.concat(dayKey).sort();
  }

  const next = selectedDayKeys.filter((key) => key !== dayKey);
  if (!next.length) {
    return allDayKeys.slice();
  }
  return next;
}

export function fuelStatsForRange(fuelRecords, startMs, endMs) {
  if (!fuelRecords || !fuelRecords.length) {
    return {
      totalLiters: 0,
      recordCount: 0,
      avgLitersPerStop: 0,
      byMachine: [],
    };
  }

  const filtered = fuelRecords.filter((row) => {
    const ts = toMs(row.timestamp);
    if (ts == null) return false;
    if (startMs != null && ts < startMs) return false;
    if (endMs != null && ts > endMs) return false;
    return true;
  });

  const byMachine = {};
  let totalLiters = 0;

  filtered.forEach((row) => {
    const ident = row.machineIdent || "—";
    const liters = Number(row.liters) || 0;
    totalLiters += liters;
    if (!byMachine[ident]) {
      byMachine[ident] = { ident, liters: 0, count: 0 };
    }
    byMachine[ident].liters += liters;
    byMachine[ident].count += 1;
  });

  const machines = Object.keys(byMachine)
    .sort()
    .map((ident) => {
      const item = byMachine[ident];
      return {
        ident,
        liters: Math.round(item.liters * 100) / 100,
        count: item.count,
        avgLiters: item.count
          ? Math.round((item.liters / item.count) * 100) / 100
          : 0,
      };
    });

  const count = filtered.length;
  return {
    totalLiters: Math.round(totalLiters * 100) / 100,
    recordCount: count,
    avgLitersPerStop: count
      ? Math.round((totalLiters / count) * 100) / 100
      : 0,
    byMachine: machines,
  };
}
